// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ** utils imports
import Transition from '../../../utils/Transition';

// ** enums imports
import { ACCOUNT_TYPES } from '../../../enums/common';

function AccountSelect(props) {
  // props
  const { onChange, currentValue } = props;

  // vars - create options with proper labels
  const options = [
    {
      label: 'All Account Types',
      value: 'ALL',
    },
    {
      label: 'Registered',
      value: ACCOUNT_TYPES.REGISTER,
    },
    {
      label: 'Guest',
      value: ACCOUNT_TYPES.GUEST,
    },
  ];

  // states
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(() => {
    // Find the option that matches currentValue, default to first option
    return options.find(option => option.value === currentValue) || options[0];
  });

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Sync selected state with currentValue prop
  useEffect(() => {
    const newSelected =
      options.find(option => option.value === currentValue) || options[0];
    setSelected(newSelected);
  }, [currentValue]);

  // hooks
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Handle option selection
  const handleOptionSelect = option => {
    setSelected(option);
    if (onChange) {
      onChange(option.value === 'ALL' ? undefined : option.value);
    }
    setDropdownOpen(false);
  };

  // Check if there's an active filter
  const hasActiveFilter = selected.value !== 'ALL';

  return (
    <div className="relative z-20">
      <button
        ref={trigger}
        className={`btn justify-between min-w-44 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 relative ${
          hasActiveFilter
            ? 'border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20'
            : ''
        }`}
        aria-label="Select account type"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            className="fill-current text-gray-400 dark:text-gray-500 shrink-0 mr-2"
          >
            <path d="M20 22H18V20C18 18.3431 16.6569 17 15 17H9C7.34315 17 6 18.3431 6 20V22H4V20C4 17.2386 6.23858 15 9 15H15C17.7614 15 20 17.2386 20 20V22ZM12 13C8.68629 13 6 10.3137 6 7C6 3.68629 8.68629 1 12 1C15.3137 1 18 3.68629 18 7C18 10.3137 15.3137 13 12 13ZM12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"></path>
          </svg>
          <span className="first-letter:uppercase">{selected.label}</span>
        </span>
        <svg
          className="shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500"
          width="8"
          height="5"
          viewBox="0 0 8 5"
        >
          <path d="M4 5L0 1h8L4 5z" />
        </svg>

        {/* Active filter indicator */}
        {hasActiveFilter && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-violet-500 rounded-full"></div>
        )}
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-20 absolute top-full right-0 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="font-medium text-sm text-gray-600 dark:text-gray-300"
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          {options.map(option => {
            return (
              <button
                key={option.value}
                tabIndex="0"
                className={`flex items-center w-full hover:bg-gray-50 dark:hover:bg-gray-700/20 py-1 px-3 cursor-pointer ${
                  option.value === selected.value && 'text-violet-500'
                }`}
                onClick={() => handleOptionSelect(option)}
              >
                <svg
                  className={`shrink-0 mr-2 fill-current text-violet-500 ${
                    option.value !== selected.value && 'invisible'
                  }`}
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                >
                  <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                </svg>
                <span className="first-letter:uppercase">{option.label}</span>
              </button>
            );
          })}
        </div>
      </Transition>
    </div>
  );
}

// PropTypes for better type checking
AccountSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  currentValue: PropTypes.string,
};

export default AccountSelect;
